import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { prepareProfileSpotlight, executeProfileSpotlightEmailUser, recordProfileSpotlight } from '@/redux/thunks/marketplaceThunks';
import { TransactionDetails } from '../components/TransactionConfirmModal';
import { buildSignBroadcast } from '@/utils/kleverTx';

interface PrepareResponse {
  payload: any;
  metadata: string;
  kdaFee?: string;
  network?: string;
  pricing: {
    pricePerHour: string;
    durationHours: number;
    totalCost: string;
    estimatedGas: string;
    totalTransaction: string;
  };
  profile: {
    id: string;
    nftProfileId: number;
    username: string;
  };
  canProceed?: boolean;
  warning?: string;
  balance?: {
    current: number;
    required: string;
    shortfall: string | null;
  };
}

interface PurchaseParams {
  profileId: string;
  durationHours: number;
  /** 'location' (default; existing marketplace behavior — targets the
   *  profile owner's state) or 'global' (surfaces on /agents/discover
   *  Spotlighted rail for every viewer, no location filter). */
  audienceScope?: 'location' | 'global';
}

export const useProfileSpotlight = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
  const [preparedData, setPreparedData] = useState<PrepareResponse | null>(null);
  // Ref (not state) — the wallet-user flow spans an async gap where
  // React re-renders don't matter, but we need the scope value at
  // record-call time. State would risk a stale-closure bug in the
  // useCallback-less handlers below.
  const scopeRef = React.useRef<'location' | 'global' | undefined>(undefined);

  /**
   * Step 1: Prepare the transaction (validate and get pricing)
   */
  const prepareTransaction = async (params: PurchaseParams): Promise<TransactionDetails> => {
    setIsLoading(true);
    setError(null);
    // Preserve audienceScope across the multi-step wallet flow so the
    // record call at the end matches what was prepared.
    scopeRef.current = params.audienceScope;

    try {
      const result = await dispatch(prepareProfileSpotlight(params)).unwrap();
      const data = result as PrepareResponse;
      setPreparedData(data);

      // Build transaction details for the modal
      const details: TransactionDetails = {
        type: 'profile_spotlight',
        title: 'Confirm Profile Spotlight',
        description: `You are about to spotlight @${data.profile.username} for ${data.pricing.durationHours} hours.`,
        mainCost: data.pricing.totalCost,
        mainCostLabel: `Spotlight Fee (${data.pricing.durationHours}hr × ${data.pricing.pricePerHour} KLYX)`,
        feeBreakdown: [
          {
            label: 'App pool receives',
            amount: (parseFloat(data.pricing.totalCost) * 0.95).toFixed(2),
            percentage: '95%',
          },
          {
            label: 'Profile creator receives',
            amount: (parseFloat(data.pricing.totalCost) * 0.05).toFixed(2),
            percentage: '5%',
          },
        ],
        estimatedGas: data.pricing.estimatedGas,
        totalCost: data.pricing.totalTransaction,
        confirmButtonText: 'Confirm & Spotlight',
        canProceed: data.canProceed !== false,
        warning: data.warning,
        balance: data.balance,
      };

      setTransactionDetails(details);
      return details;
    } catch (err: any) {
      const errorMsg = err || 'Failed to prepare transaction';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 2: Execute the transaction (sign with wallet extension)
   */
  const executeTransaction = async (): Promise<string> => {
    if (!preparedData) {
      throw new Error('Transaction not prepared');
    }

    setIsLoading(true);
    setError(null);

    try {
      const txHash = await buildSignBroadcast({
        payload: preparedData.payload,
        metadata: preparedData.metadata,
        network: preparedData.network,
        kdaFee: preparedData.kdaFee,
      });
      return txHash;
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to sign transaction';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Step 3: Record the purchase in the database
   */
  const recordPurchase = async (txHash: string): Promise<void> => {
    if (!preparedData) {
      throw new Error('Transaction not prepared');
    }

    setIsLoading(true);
    setError(null);

    try {
      await dispatch(
        recordProfileSpotlight({
          profileId: preparedData.profile.id,
          durationHours: preparedData.pricing.durationHours,
          txHash,
          audienceScope: scopeRef.current,
        })
      ).unwrap();
    } catch (err: any) {
      const errorMsg = err || 'Failed to record purchase';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Complete flow for email users: backend handles everything —
   * signs with the custodial wallet, broadcasts, AND writes the
   * spotlight_promotion row in the same handler
   * (executeProfileSpotlightEmailUser at
   * server/src/controllers/Marketplace/MarketplaceController.ts).
   *
   * The wallet flow splits sign-and-broadcast (browser) from record
   * (server) because the server doesn't hold the wallet's key. For
   * email users the server does hold it, so it commits the row
   * atomically with the broadcast and there's no follow-up record
   * call — a record here would replay the same txHash and get
   * rejected by the SpotlightVerificationService replay guard.
   */
  const executeEmailUserFlow = async (params: PurchaseParams): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await dispatch(
        executeProfileSpotlightEmailUser({
          profileId: params.profileId,
          durationHours: params.durationHours,
          audienceScope: params.audienceScope,
        })
      ).unwrap();
    } catch (err: any) {
      // Check if error contains structured pricing data (insufficient funds)
      if (err && typeof err === 'object' && err.pricing && err.balance) {
        const detailedError = `Insufficient funds. You need ${err.balance.required} KLYX (current balance: ${err.balance.current.toFixed(2)} KLYX, shortfall: ${err.balance.shortfall} KLYX). ${err.message || ''}`;
        setError(detailedError);
        throw new Error(detailedError);
      }
      const errorMsg = err || 'Failed to complete spotlight';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Complete flow: prepare -> execute -> record
   */
  const purchaseSpotlight = async (params: PurchaseParams): Promise<void> => {
    try {
      // Check if email user
      if (user?.authMethod === 'email') {
        // Email users: backend handles everything
        await executeEmailUserFlow(params);
        return;
      }

      // Wallet users: Step 1 - Prepare
      await prepareTransaction(params);

      // Modal shows here - executeTransaction will be called when user confirms

      // Steps 2 & 3 are called separately after user confirmation
    } catch (err) {
      // Error is already set in prepareTransaction or executeEmailUserFlow
      throw err;
    }
  };

  const reset = () => {
    setIsLoading(false);
    setError(null);
    setTransactionDetails(null);
    setPreparedData(null);
    scopeRef.current = undefined;
  };

  return {
    isLoading,
    error,
    transactionDetails,
    prepareTransaction,
    executeTransaction,
    recordPurchase,
    purchaseSpotlight,
    reset,
  };
};

export default useProfileSpotlight;
